"use client";
import React from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';

interface Criterion {
    label: string;
    value: string;
    icon: React.ReactNode;
}

const criteria: Criterion[] = [
    {
        label: 'Job Function',
        value: 'Marketing, IT, Ops',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="8" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M9 8V6a3 3 0 013-3v0a3 3 0 013 3v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        label: 'Seniority',
        value: 'Director & above',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="14" width="4" height="6" rx="1" stroke="currentColor" strokeWidth="2" />
                <rect x="10" y="9" width="4" height="11" rx="1" stroke="currentColor" strokeWidth="2" />
                <rect x="16" y="4" width="4" height="16" rx="1" stroke="currentColor" strokeWidth="2" />
            </svg>
        ),
    },
    {
        label: 'Industry',
        value: 'SaaS, Fintech, B2B',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="6" width="10" height="14" rx="1" stroke="currentColor" strokeWidth="2" />
                <rect x="14" y="10" width="6" height="10" rx="1" stroke="currentColor" strokeWidth="2" />
                <path d="M7 9.5h1M10 9.5h1M7 13h1M10 13h1M7 16.5h1M10 16.5h1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        label: 'Company Size',
        value: '200+ employees',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="2" />
                <circle cx="17" cy="10" r="2" stroke="currentColor" strokeWidth="2" />
                <path d="M4 19c0-2.8 2.2-5 5-5s5 2.2 5 5M14.5 19c0-2.2 1.6-4 3.7-4.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        label: 'Company Revenue',
        value: '$10M+ annual',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
                <path d="M12 7.5v9M14.5 9.8c0-1-1-1.8-2.5-1.8s-2.5.8-2.5 1.8 1 1.5 2.5 1.7 2.5.7 2.5 1.7-1 1.8-2.5 1.8-2.5-.8-2.5-1.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        label: 'Skills',
        value: 'Demand Gen, ABM',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M12 4l2.3 4.8 5.2.7-3.8 3.7.9 5.3L12 15.9l-4.6 2.6.9-5.3-3.8-3.7 5.2-.7L12 4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        label: 'Groups & Interests',
        value: 'B2B Growth, MarTech',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="8.5" cy="9" r="3" stroke="currentColor" strokeWidth="2" />
                <circle cx="16" cy="9" r="2.2" stroke="currentColor" strokeWidth="2" />
                <path d="M3.5 19c0-3 2.2-5.3 5-5.3s5 2.3 5 5.3M14.5 14.2c2 .2 3.5 2 3.5 4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
    },
];

function AudienceBuilderPanel() {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="w-full max-w-[440px] mx-auto rounded-3xl overflow-hidden"
            style={{ background: '#fbfdfd', border: '1px solid rgba(8,4,0,0.08)', boxShadow: '0 30px 70px -30px rgba(55,48,163,0.35)' }}
        >
            {/* Window chrome */}
            <div className="flex items-center gap-2 px-5 py-3.5" style={{ borderBottom: '1px solid rgba(8,4,0,0.08)', background: 'rgba(55,48,163,0.03)' }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'rgba(8,4,0,0.14)' }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'rgba(8,4,0,0.14)' }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'rgba(8,4,0,0.14)' }} />
                <span className="ml-2 text-[11px] font-bold uppercase tracking-[0.08em]" style={{ fontFamily: CLASH, color: 'rgba(8,4,0,0.4)' }}>
                    Audience Builder
                </span>
            </div>

            {/* Criteria list */}
            <div className="flex flex-col">
                {criteria.map((c, i) => (
                    <motion.div
                        key={c.label}
                        initial={{ opacity: 0, x: -12 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.1 + i * 0.07 }}
                        className="flex items-center gap-3 px-5 py-3"
                        style={{ borderBottom: i < criteria.length - 1 ? '1px solid rgba(8,4,0,0.06)' : 'none' }}
                    >
                        <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                            style={{ background: 'rgba(55,48,163,0.08)', color: PURPLE }}
                        >
                            {c.icon}
                        </div>
                        <div className="flex flex-col min-w-0">
                            <span className="text-sm font-bold leading-tight" style={{ fontFamily: CLASH, color: INK }}>
                                {c.label}
                            </span>
                            <span className="text-xs truncate" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.45)' }}>
                                {c.value}
                            </span>
                        </div>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="ml-auto shrink-0">
                            <circle cx="12" cy="12" r="10" fill="#ceff1a" />
                            <path d="M8 12.5l2.5 2.5L16 9.5" stroke="#080400" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </motion.div>
                ))}
            </div>

            {/* Estimated audience footer */}
            <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="flex items-center justify-between px-5 py-4"
                style={{ background: PURPLE }}
            >
                <span className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.7)' }}>
                    Estimated Audience
                </span>
                <span className="text-lg font-bold" style={{ fontFamily: CLASH, color: '#ffffff' }}>
                    42,000+
                </span>
            </motion.div>
        </motion.div>
    );
}

export default function LinkedInAdsWhatIs() {
    return (
        <section className="relative overflow-hidden pt-20 pb-16 md:pt-28 md:pb-20 px-6 md:px-16 lg:px-24" style={{ background: '#ffffff' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.035]" style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1200px] mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                <div className="lg:col-span-7 flex flex-col gap-6">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: PURPLE, fontFamily: CLASH }}
                    >
                        The Basics
                    </motion.span>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: INK }}
                    >
                        What Are LinkedIn Ads?
                    </motion.h2>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="flex flex-col gap-5"
                    >
                        <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}>
                            LinkedIn Ads allow businesses to reach professional audiences based on characteristics such as job function, seniority, industry and company attributes.
                        </p>
                        <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}>
                            This makes LinkedIn particularly useful for businesses selling complex products, services or solutions where the person seeing the advertisement can influence or make a purchasing decision.
                        </p>
                    </motion.div>
                </div>

                <div className="lg:col-span-5">
                    <AudienceBuilderPanel />
                </div>
            </div>
        </section>
    );
}
